<template>
  <div class="channel-token-management">
    <!-- 회사 및 채널 목록 -->
    <div class="section-header">
      <h3>비즈메세지 인증 관리</h3>
      <a-space>
        <a-button
          type="primary"
          class="yellow-button"
          @click="handleAddCompany"
        >
          <plus-outlined /> 인증정보 추가
        </a-button>
        <a-button
          @click="fetchData"
          :loading="loading"
        >
          <reload-outlined /> 새로고침
        </a-button>
      </a-space>
    </div>

    <a-table
      class="ant-table-striped"
      size="small"
      :columns="columns"
      :data-source="companyList"
      :pagination="false"
      :loading="loading"
      :row-key="record => record._id"
    >
      <!-- 회사명 -->
      <template #companyName="{ text }">
        <span style="font-weight: 500;">{{ text || '-' }}</span>
      </template>

      <!-- 채널 정보 -->
      <template #channels="{ record }">
        <div v-if="record.channels && record.channels.length > 0" class="channels-container">
          <div v-for="(channel, index) in record.channels" :key="channel.channelId" class="channel-row">
            <a-tag color="blue">{{ channel.channelId }}</a-tag>
          </div>
        </div>
        <div v-else class="channels-container">
          <span style="color: #999;">채널 없음</span>
        </div>
      </template>

      <!-- 등록일시 -->
      <template #createdAt="{ text }">
        {{ formatDateTime(text) }}
      </template>

      <!-- 수정일시 -->
      <template #updatedAt="{ text }">
        {{ formatDateTime(text) }}
      </template>

      <!-- 작업 -->
      <template #action="{ record }">
        <a-space>
          <a-button
            type="link"
            size="small"
            @click="handleViewCompany(record)"
          >
            상세
          </a-button>
          <a-button
            type="link"
            size="small"
            @click="handleEditCompany(record)"
          >
            수정
          </a-button>
          <a-button
            type="link"
            size="small"
            danger
            @click="handleDeleteCompany(record)"
          >
            삭제
          </a-button>
        </a-space>
      </template>

      <!-- 빈 데이터 -->
      <template #emptyText>
        <div class="noResult">등록된 회사 정보가 없습니다.</div>
      </template>
    </a-table>

    <!-- 회사 상세 드로워 -->
    <CompanyDetailDrawer
      v-model:visible="drawerVisible"
      :company-data="selectedCompany"
      @close="handleCloseDrawer"
      @refresh="handleRefresh"
    />

    <!-- 회사 정보 추가/수정 드로워 -->
    <CompanyFormDrawer
      v-model:visible="companyFormVisible"
      :company-data="selectedCompanyForEdit"
      :mode="companyFormMode"
      @save="handleSaveCompany"
      @close="handleCloseCompanyForm"
    />
  </div>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue';
import { message, Modal } from 'ant-design-vue';
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons-vue';
import { BizmsgAuthApi } from '../../api';
import { twoBtnDialog } from '@/utils/utils';
import CompanyDetailDrawer from './CompanyDetailDrawer.vue';
import CompanyFormDrawer from './CompanyFormDrawer.vue';

export default defineComponent({
  name: 'ChannelTokenManagement',
  components: {
    ReloadOutlined,
    PlusOutlined,
    CompanyDetailDrawer,
    CompanyFormDrawer
  },
  setup() {
    const loading = ref(false);
    const drawerVisible = ref(false);
    const selectedCompany = ref(null);

    const companyFormVisible = ref(false);
    const companyFormMode = ref('create'); // 'create', 'edit'
    const selectedCompanyForEdit = ref(null);

    const companyList = ref([]);

    const columns = [
      {
        title: '번호',
        width: 60,
        customRender: ({ index }) => index + 1
      },
      {
        title: '회사명',
        dataIndex: 'companyName',
        key: 'companyName',
        width: 150,
        slots: { customRender: 'companyName' }
      },
      {
        title: '회사 ID',
        dataIndex: 'companyId',
        key: 'companyId',
        width: 200
      },
      {
        title: '채널 정보',
        key: 'channels',
        width: 200,
        align: 'center',
        slots: { customRender: 'channels' }
      },
      {
        title: '등록일시',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 170,
        slots: { customRender: 'createdAt' }
      },
      {
        title: '수정일시',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 170,
        slots: { customRender: 'updatedAt' }
      },
      {
        title: '수정자',
        dataIndex: 'updatedBy',
        key: 'updatedBy',
        width: 100
      },
      {
        title: '작업',
        key: 'action',
        width: 180,
        fixed: 'right',
        slots: { customRender: 'action' }
      }
    ];

    // 데이터 조회
    const fetchData = async () => {
      try {
        loading.value = true;

        const response = await BizmsgAuthApi.getAllBizmsgAuth();

        // 배열 응답을 직접 사용
        if (Array.isArray(response)) {
          companyList.value = response;
        } else {
          console.error('응답 구조 오류:', response);
          message.error('데이터 조회에 실패했습니다.');
        }
      } catch (error) {
        console.error('데이터 조회 오류:', error);
        message.error('데이터 조회 중 오류가 발생했습니다.');
      } finally {
        loading.value = false;
      }
    };

    // 날짜 포맷팅
    const formatDateTime = (dateStr) => {
      if (!dateStr) return '-';

      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    // 토큰 상태 색상
    const getTokenStatusColor = (expiresAt) => {
      if (!expiresAt) return 'default';

      const now = new Date();
      const expireDate = new Date(expiresAt);
      const diffHours = (expireDate - now) / (1000 * 60 * 60);

      if (diffHours < 0) return 'error'; // 만료됨
      if (diffHours < 24) return 'warning'; // 24시간 이내
      return 'success'; // 정상
    };

    // 토큰 상태 텍스트
    const getTokenStatusText = (expiresAt) => {
      if (!expiresAt) return '미등록';

      const now = new Date();
      const expireDate = new Date(expiresAt);
      const diffHours = (expireDate - now) / (1000 * 60 * 60);

      if (diffHours < 0) return '만료됨';
      if (diffHours < 24) return '곧 만료';
      return '정상';
    };

    // 회사 상세 보기
    const handleViewCompany = (company) => {
      selectedCompany.value = { ...company };
      drawerVisible.value = true;
    };

    // 드로워 닫기
    const handleCloseDrawer = () => {
      drawerVisible.value = false;
      selectedCompany.value = null;
    };

    // 드로워에서 데이터 갱신 시 처리
    const handleRefresh = (updatedData) => {
      // 선택된 회사 데이터 업데이트
      selectedCompany.value = updatedData;

      // 전체 목록에서도 해당 회사 데이터 업데이트
      const index = companyList.value.findIndex(c => c.companyId === updatedData.companyId);
      if (index !== -1) {
        companyList.value[index] = updatedData;
      }
    };

    // 회사 추가
    const handleAddCompany = () => {
      companyFormMode.value = 'create';
      selectedCompanyForEdit.value = null;
      companyFormVisible.value = true;
    };

    // 회사 수정
    const handleEditCompany = (company) => {
      companyFormMode.value = 'edit';
      selectedCompanyForEdit.value = { ...company };
      companyFormVisible.value = true;
    };

    // 회사 삭제
    const handleDeleteCompany = async (company) => {
      const confirmed = await twoBtnDialog(
        '인증정보 삭제',
        `"${company.companyName}" (${company.companyId})의 인증 정보를 삭제하시겠습니까?\n모든 채널 정보가 함께 삭제됩니다.`,
        '삭제'
      );

      if (!confirmed) return;

      try {
        await BizmsgAuthApi.deleteBizmsgAuth(company.companyId);
        message.success('인증 정보가 삭제되었습니다.');
        await fetchData();
      } catch (error) {
        console.error('인증 정보 삭제 오류:', error);
        message.error('인증 정보 삭제 중 오류가 발생했습니다.');
      }
    };

    // 회사 정보 저장
    const handleSaveCompany = async (companyData) => {
      try {
        if (companyFormMode.value === 'create') {
          await BizmsgAuthApi.createBizmsgAuth(companyData);
          message.success('인증 정보가 추가되었습니다.');
        } else {
          // 수정 시에는 companyId를 body에서 제거 (URL 경로에 이미 포함됨)
          const { companyId, ...updateData } = companyData;
          await BizmsgAuthApi.updateBizmsgAuth(companyId, updateData);
          message.success('인증 정보가 수정되었습니다.');
        }

        companyFormVisible.value = false;
        await fetchData();
      } catch (error) {
        console.error('인증 정보 저장 오류:', error);
        if (error.response?.status === 409) {
          message.error('이미 존재하는 회사 ID입니다.');
        } else if (error.response?.status === 404) {
          message.error('회사 정보를 찾을 수 없습니다.');
        } else if (error.response?.status === 400) {
          message.error(error.response?.data?.message || '입력 데이터가 올바르지 않습니다.');
        } else {
          message.error('인증 정보 저장 중 오류가 발생했습니다.');
        }
      }
    };

    // 회사 폼 드로워 닫기
    const handleCloseCompanyForm = () => {
      companyFormVisible.value = false;
      selectedCompanyForEdit.value = null;
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      drawerVisible,
      selectedCompany,
      companyFormVisible,
      companyFormMode,
      selectedCompanyForEdit,
      companyList,
      columns,
      fetchData,
      formatDateTime,
      getTokenStatusColor,
      getTokenStatusText,
      handleViewCompany,
      handleCloseDrawer,
      handleRefresh,
      handleAddCompany,
      handleEditCompany,
      handleDeleteCompany,
      handleSaveCompany,
      handleCloseCompanyForm
    };
  }
});
</script>

<style scoped>
.channel-token-management {
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.noResult {
  text-align: center;
  padding: 20px;
  color: #999;
}

/* 채널 정보 정렬 */
.channels-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.channel-row {
  margin-bottom: 8px;
}

.channel-row:last-child {
  margin-bottom: 0;
}

:deep(.ant-table-striped) :deep(.ant-table-body) tr:nth-child(2n) td {
  background-color: #fafafa;
}

.yellow-button {
  background-color: #FEE500;
  border-color: #FEE500;
  color: #000000;
}

.yellow-button:hover {
  background-color: #FFD700;
  border-color: #FFD700;
  color: #000000;
}
</style>
